import searchClient from "@/lib/services/algolia";
import {
  createSearchParams,
  createPortfolioSearchParams,
  getIndexNames,
} from "../config/algoliaConfig";
import { transformSearchResults } from "../utils/searchTransformers";
import {
  enrichByEmail,
  enrichByLinkedInUrl,
  enrichByNameAndCompany,
  transformEnrichToSearchResult,
  saveEnrichedToPortfolios,
} from "../services/searchEnrichment";
import { extractLinkedInVanity } from "@/app/components/utils/linkedinUtils";
import {
  ensureSearchVisitorSession,
  readExternalSearchCooldown,
} from "../utils/searchAbuse";

async function readSearchResponse(response, onRcCount) {
  if (
    !response.headers.get("content-type")?.includes("application/x-ndjson") ||
    !response.body
  ) {
    return response.json();
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamedResult = null;
  const processLine = (line) => {
    if (!line.trim()) return;
    const event = JSON.parse(line);
    if (event.type === "count") {
      onRcCount?.(event.count);
    } else if (event.type === "results") {
      streamedResult = event.data;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    lines.forEach(processLine);
    if (done) break;
  }

  buffer += decoder.decode();
  processLine(buffer);
  return streamedResult || { success: false };
}

export const useSearchAPI = () => {
  // Helper function to deduplicate results (used for name and email searches)
  const deduplicateResults = (results) => {
    const seen = new Map();
    const deduplicated = [];

    for (const result of results) {
      const linkedinId = result.id || null;

      if (!linkedinId) {
        // Ensure non-LinkedIn results don't have enriched flag unless they're actually enriched
        const cleanResult = { ...result };
        if (!result.enriched) {
          delete cleanResult.enriched;
        }
        deduplicated.push(cleanResult);
        continue;
      }

      if (!seen.has(linkedinId)) {
        // Clean enriched flag from users profiles (keep it only for portfolios)
        const cleanResult = { ...result };
        if (!result.enriched) {
          delete cleanResult.enriched;
        }

        seen.set(linkedinId, cleanResult);
        deduplicated.push(cleanResult);
      } else {
        // If duplicate LinkedIn ID found, prioritize users over portfolios
        const existing = seen.get(linkedinId);

        if (!existing.enriched && result.enriched) {
          // Keep existing (users) over enriched (portfolios)
          continue;
        } else if (existing.enriched && !result.enriched) {
          // Replace enriched with users
          const cleanResult = { ...result };
          delete cleanResult.enriched; // Ensure users profiles don't have enriched flag

          const index = deduplicated.findIndex((r) => r === existing);
          if (index !== -1) {
            deduplicated[index] = cleanResult;
            seen.set(linkedinId, cleanResult);
          }
        }
      }
    }

    return deduplicated;
  };

  const performSearch = async (
    searchType,
    refinedQuery,
    isNameOnly = false,
    separatedQueries = null,
    skipEnrichment = false,
  ) => {
    const indexNames = getIndexNames();

    // For name and email searches - SEARCH BOTH INDEXES WITH DEDUPLICATION
    if (
      searchType === "name" ||
      searchType === "email" ||
      (searchType === "company" && separatedQueries)
    ) {
      const queries = separatedQueries || {
        nameQuery: searchType === "email" ? "" : refinedQuery,
        companyQuery: "",
        emailQuery: searchType === "email" ? refinedQuery : "",
      };

      const searchResults = await tryBothIndexesSearch(
        queries,
        searchType,
        indexNames,
      );

      if (searchResults.nbHits > 0) {
        return {
          transformedResults: searchResults.transformedResults,
          nbHits: searchResults.nbHits,
        };
      } else {
        // No results found in database
        // Try enrichment for name+company or email
        if (
          (searchType === "company" &&
            queries.companyQuery &&
            queries.companyQuery.trim()) ||
          searchType === "email"
        ) {
          try {
            let enrichData;

            if (searchType === "email") {
              enrichData = await enrichByEmail(refinedQuery);
            } else {
              enrichData = await enrichByNameAndCompany(
                queries.nameQuery,
                queries.companyQuery,
              );
            }

            // DB cache hit — return directly, no re-save needed
            if (enrichData?.success && enrichData?.source === "db") {
              return {
                transformedResults: [enrichData.transformedResult],
                nbHits: 1,
                externalSearchCooldown: enrichData.externalSearchCooldown || null,
              };
            }

            // Handle enrichment failure gracefully
            if (!enrichData?.success) {
              return {
                transformedResults: [],
                nbHits: 0,
                enrichmentFailed: true,
                enrichmentError: enrichData?.error,
                externalSearchCooldown: enrichData?.externalSearchCooldown,
              };
            }

            // If enrichment was successful
            if (enrichData?.success && enrichData?.enrichData?.person) {
              // Transform enriched data to search result format + get AI salary
              const transformedResult = await transformEnrichToSearchResult(
                enrichData,
              );

              // Save to portfolios collection
              try {
                const saveQuery =
                  searchType === "email"
                    ? refinedQuery
                    : `${queries.nameQuery} ${queries.companyQuery}`;

                const saveResult = await saveEnrichedToPortfolios(
                  transformedResult,
                  saveQuery,
                  searchType,
                  enrichData.personAllowanceToken,
                );
              } catch (saveError) {
                console.error("Failed to save to Firestore:", saveError);
              }

              // Return as search results with enriched flag
              return {
                transformedResults: [
                  {
                    ...transformedResult,
                    enriched: true,
                    externalEnriched: true,
                  },
                ],
                nbHits: 1,
                enriched: true,
              };
            }
          } catch (error) {
            console.error("Enrichment failed:", error);
          }
        }

        // If enrichment also failed or not applicable, return empty results
        return {
          transformedResults: [],
          nbHits: 0,
          enrichmentFailed: true,
          ...(searchType === "name" && searchResults.searchFailed
            ? { indexSearchFailed: true }
            : {}),
        };
      }
    }

    const searchParams = createSearchParams(searchType, refinedQuery);

    // Search users index first
    let searchResults = await searchClient.searchSingleIndex({
      indexName: indexNames.users,
      searchParams,
    });

    // Check if results have valid id field
    if (searchResults.nbHits > 0) {
      const validResults = searchResults.hits.filter(
        (hit) => hit.id && hit.id.trim() !== "",
      );

      if (validResults.length > 0) {
        return {
          transformedResults: transformSearchResults(validResults),
          nbHits: validResults.length,
        };
      }
    }

    // If no valid results found in users index, fallback to portfolios index
    if (
      searchResults.nbHits === 0 ||
      searchResults.hits.filter((hit) => hit.id && hit.id.trim() !== "")
        .length === 0
    ) {
      searchResults = await searchClient.searchSingleIndex({
        indexName: indexNames.portfolios,
        searchParams: createPortfolioSearchParams(searchParams),
      });

      if (searchResults.nbHits > 0) {
        const validResults = searchResults.hits.filter(
          (hit) => hit.id && hit.id.trim() !== "",
        );

        if (validResults.length > 0) {
          return {
            transformedResults: transformSearchResults(validResults, true),
            nbHits: validResults.length,
          };
        }
      }
    }

    // For LinkedIn searches, try additional search patterns before enrichment
    if (searchType === "linkedin") {
      const additionalResults = await tryAdditionalLinkedInSearch(
        refinedQuery,
        indexNames,
      );
      if (additionalResults.nbHits > 0) {
        const validAdditionalResults = additionalResults.hits.filter(
          (hit) => hit.id && hit.id.trim() !== "",
        );

        if (validAdditionalResults.length > 0) {
          return {
            transformedResults: transformSearchResults(
              validAdditionalResults,
              true,
            ),
            nbHits: validAdditionalResults.length,
          };
        }
      }
    }

    // If still no valid results, handle based on search type
    if (
      searchResults.nbHits === 0 ||
      searchResults.hits.filter((hit) => hit.id && hit.id.trim() !== "")
        .length === 0
    ) {
      // For name-only searches, don't try enrichment - show empty results with message
      if (isNameOnly) {
        return {
          transformedResults: [],
          nbHits: 0,
          isNameOnly: true,
        };
      }

      // For LinkedIn, try enrichment
      try {
        let enrichData;

        if (searchType === "linkedin") {
          enrichData = await enrichByLinkedInUrl(refinedQuery);
        } else {
          return {
            transformedResults: [],
            nbHits: 0,
          };
        }

        // DB cache hit — return directly, no re-save needed
        if (enrichData?.success && enrichData?.source === "db") {
          return {
            transformedResults: [enrichData.transformedResult],
            nbHits: 1,
            externalSearchCooldown: enrichData.externalSearchCooldown || null,
          };
        }

        if (enrichData?.externalSearchCooldown) {
          return {
            transformedResults: [],
            nbHits: 0,
            enrichmentFailed: true,
            externalSearchCooldown: enrichData.externalSearchCooldown,
          };
        }

        // If enrichment was successful
        if (enrichData?.success && enrichData?.enrichData?.person) {
          // Transform enriched data to search result format + get AI salary
          const transformedResult = await transformEnrichToSearchResult(
            enrichData,
          );

          // Save to portfolios collection
          await saveEnrichedToPortfolios(
            transformedResult,
            refinedQuery,
            searchType,
            enrichData.personAllowanceToken,
          );

          // Return as search results with enriched flag
          return {
            transformedResults: [
              {
                ...transformedResult,
                enriched: true,
                externalEnriched: true,
              },
            ],
            nbHits: 1,
            enriched: true,
          };
        }
      } catch (error) {
        console.error("Enrichment fallback failed:", error);
        throw error;
      }
    }

    // Return empty results if everything failed
    return {
      transformedResults: [],
      nbHits: 0,
    };
  };

  // Handle both name and email searches with deduplication
  const tryBothIndexesSearch = async (queries, searchType, indexNames) => {
    // For email searches, use email-specific search logic
    if (searchType === "email") {
      return await tryEmailSearchBoth(queries.emailQuery, indexNames);
    }

    // For name searches, use existing logic
    return await tryNameCompanySearchBoth(
      queries.nameQuery,
      queries.companyQuery || "",
      indexNames,
    );
  };

  const tryEmailSearchBoth = async (emailQuery, indexNames) => {
    if (!emailQuery.trim()) return { nbHits: 0, transformedResults: [] };
    const emailSearchParams = createSearchParams("email", emailQuery);

    // Define email-specific attributes to retrieve
    const emailAttributes = [
      "linkedin.title",
      "linkedin.headline",
      "profile.given_name",
      "profile.family_name",
      "profile.picture",
      "profile.picture_custom",
      "profile.picture_cached",
      "profile.linkedin_url",
      "profile.affiliation_name",
      "profile.affiliation_url",
      "profile.title",
      "profile.expertise",
      "profile.accept_percent",
      "profile.response_rate",
      "stats.meeting_stats.received.requested",
      "stats.meeting_stats.received.dismissed",
      "stats.meeting_stats.received.completed",
      "stats.meeting_stats.sent.completed",
      "profile.response_rate",
      "profile.rateSettings",
      "profile.sessionTypes",
      "profile.meetingTypes",
      "profile.country_code",
      "profile.hourly_rate",
      "profile.half_rate",
      "profile.is_public",
      "profile.url",
      "company.name",
      "company.logo",
      "company.url",
      "contacts.email",
      "email",
      "id",
      "sub",
      "vanity",
    ];

    // Search both indexes simultaneously for email
    const [usersResults, portfoliosResults] = await Promise.all([
      searchClient
        .searchSingleIndex({
          indexName: indexNames.users,
          searchParams: {
            ...emailSearchParams,
            attributesToRetrieve: emailAttributes,
          },
        })
        .catch((error) => {
          console.error("Users email search failed:", error);
          return { nbHits: 0, hits: [] };
        }),
      searchClient
        .searchSingleIndex({
          indexName: indexNames.portfolios,
          searchParams: {
            ...emailSearchParams,
            attributesToRetrieve: emailAttributes,
          },
        })
        .catch((error) => {
          console.error("Portfolios email search failed:", error);
          return { nbHits: 0, hits: [] };
        }),
    ]);

    let allResults = [];

    // Process users results
    if (usersResults.nbHits > 0) {
      const validResults = usersResults.hits.filter(
        (hit) => hit.id && hit.id.trim() !== "",
      );

      if (validResults.length > 0) {
        const transformedUsers = transformSearchResults(validResults, false);
        allResults.push(...transformedUsers);
      }
    }

    // Process portfolios results
    if (portfoliosResults.nbHits > 0) {
      const validResults = portfoliosResults.hits.filter(
        (hit) => hit.id && hit.id.trim() !== "",
      );

      if (validResults.length > 0) {
        const transformedPortfolios = transformSearchResults(
          validResults,
          true,
        );
        allResults.push(...transformedPortfolios);
      }
    }

    // Deduplicate results for email searches
    if (allResults.length > 0) {
      const deduplicatedResults = deduplicateResults(allResults);
      return {
        transformedResults: deduplicatedResults,
        nbHits: deduplicatedResults.length,
      };
    }

    return { nbHits: 0, transformedResults: [] };
  };

  // NAME+COMPANY SEARCHES USE BOTH INDEXES
  const tryNameCompanySearchBoth = async (
    nameQuery,
    companyQuery,
    indexNames,
  ) => {
    if (!nameQuery.trim()) return { nbHits: 0, transformedResults: [] };

    // Define consistent flattened attributes
    const flattenedAttributes = [
      "linkedin.title",
      "linkedin.headline",
      "profile.given_name",
      "profile.family_name",
      "profile.picture",
      "profile.picture_custom",
      "profile.picture_cached",
      "profile.linkedin_url",
      "profile.affiliation_name",
      "profile.affiliation_url",
      "profile.title",
      "profile.expertise",
      "profile.accept_percent",
      "profile.response_rate",
      "stats.meeting_stats.received.requested",
      "stats.meeting_stats.received.dismissed",
      "stats.meeting_stats.received.completed",
      "stats.meeting_stats.sent.completed",
      "profile.rateSettings",
      "profile.sessionTypes",
      "profile.meetingTypes",
      "profile.country_code",
      "profile.hourly_rate",
      "profile.half_rate",
      "profile.is_public",
      "profile.url",
      "company.name",
      "company.logo",
      "company.url",
      "contacts.email",
      "id",
      "sub",
    ];

    const portfolioAttributes = [...flattenedAttributes, "vanity"];

    const searchParams = {
      query: nameQuery,
      restrictSearchableAttributes: [
        "profile.given_name",
        "profile.family_name",
      ],
      typoTolerance: true,
      hitsPerPage: 20,
    };

    // Search both indexes simultaneously for name searches
    const [usersResults, portfoliosResults] = await Promise.all([
      searchClient
        .searchSingleIndex({
          indexName: indexNames.users,
          searchParams: {
            ...searchParams,
            attributesToRetrieve: flattenedAttributes,
          },
        })
        .catch((error) => {
          console.error("Users name search failed:", error);
          return { nbHits: 0, hits: [], searchFailed: true };
        }),
      searchClient
        .searchSingleIndex({
          indexName: indexNames.portfolios,
          searchParams: {
            ...searchParams,
            attributesToRetrieve: portfolioAttributes,
          },
        })
        .catch((error) => {
          console.error("Portfolios name search failed:", error);
          return { nbHits: 0, hits: [], searchFailed: true };
        }),
    ]);

    let allResults = [];

    // Process users results
    if (usersResults.nbHits > 0) {
      let validResults = usersResults.hits.filter(
        (hit) => hit.id && hit.id.trim() !== "",
      );

      // Filter by company if provided (only if company is not empty)
      if (companyQuery && companyQuery.trim()) {
        validResults = validResults.filter((hit) => {
          const companyName = hit["company.name"] || "";
          const affiliationName = hit["profile.affiliation_name"] || "";
          const searchCompany = companyQuery.toLowerCase();
          return (
            companyName.toLowerCase().includes(searchCompany) ||
            affiliationName.toLowerCase().includes(searchCompany)
          );
        });
      }

      if (validResults.length > 0) {
        const transformedUsers = transformSearchResults(validResults, false);
        allResults.push(...transformedUsers);
      }
    }

    // Process portfolios results
    if (portfoliosResults.nbHits > 0) {
      let validResults = portfoliosResults.hits.filter(
        (hit) => hit.id && hit.id.trim() !== "",
      );

      // Filter by company if provided (only if company is not empty)
      if (companyQuery && companyQuery.trim()) {
        validResults = validResults.filter((hit) => {
          const companyName = hit["company.name"] || "";
          const affiliationName = hit["profile.affiliation_name"] || "";
          const searchCompany = companyQuery.toLowerCase();
          return (
            companyName.toLowerCase().includes(searchCompany) ||
            affiliationName.toLowerCase().includes(searchCompany)
          );
        });
      }

      if (validResults.length > 0) {
        const transformedPortfolios = transformSearchResults(
          validResults,
          true,
        );
        allResults.push(...transformedPortfolios);
      }
    }

    // Deduplicate results for name searches
    if (allResults.length > 0) {
      const deduplicatedResults = deduplicateResults(allResults);
      return {
        transformedResults: deduplicatedResults,
        nbHits: deduplicatedResults.length,
      };
    }

    return {
      nbHits: 0,
      transformedResults: [],
      searchFailed: usersResults.searchFailed || portfoliosResults.searchFailed,
    };
  };

  // Helper function to try additional LinkedIn search patterns
  const tryAdditionalLinkedInSearch = async (linkedinUrl, indexNames) => {
    const vanity = extractLinkedInVanity(linkedinUrl);

    if (!vanity) return { nbHits: 0, hits: [] };

    // Define consistent flattened attributes for LinkedIn search
    const linkedinAttributes = [
      "profile.given_name",
      "profile.family_name",
      "profile.picture",
      "profile.picture_custom",
      "profile.picture_cached",
      "profile.linkedin_url",
      "profile.affiliation_name",
      "profile.affiliation_url",
      "profile.title",
      "profile.expertise",
      "profile.accept_percent",
      "profile.response_rate",
      "stats.meeting_stats.received.requested",
      "stats.meeting_stats.received.dismissed",
      "stats.meeting_stats.received.completed",
      "stats.meeting_stats.sent.completed",
      "profile.rateSettings",
      "profile.sessionTypes",
      "profile.meetingTypes",
      "profile.country_code",
      "profile.hourly_rate",
      "profile.half_rate",
      "profile.is_public",
      "linkedin.vanity",
      "linkedin.premium",
      "linkedin.verified",
      "linkedin.follower",
      "linkedin.headline",
      "linkedin.open_work",
      "linkedin.summary",
      "linkedin.title",
      "linkedin.member_id",
      "linkedin.pronoun",
      "linkedin.url",
      "id",
      "vanity",
    ];

    // Try multiple search patterns for portfolios index
    const searchPatterns = [
      // Search by vanity only
      {
        query: "",
        filters: `vanity:"${vanity}"`,
        attributesToRetrieve: linkedinAttributes,
      },
      // Search by LinkedIn vanity in nested structure
      {
        query: "",
        filters: `linkedin.vanity:"${vanity}"`,
        attributesToRetrieve: linkedinAttributes,
      },
      // Search by LinkedIn URL in nested structure
      {
        query: "",
        filters: `linkedin.url:"https://www.linkedin.com/in/${vanity}" OR linkedin.url:"https://linkedin.com/in/${vanity}" OR linkedin.url:"linkedin.com/in/${vanity}"`,
        attributesToRetrieve: linkedinAttributes,
      },
      // Search by profile.linkedin_url (stored by the enrich route)
      {
        query: "",
        filters: `profile.linkedin_url:"https://www.linkedin.com/in/${vanity}" OR profile.linkedin_url:"https://linkedin.com/in/${vanity}"`,
        attributesToRetrieve: linkedinAttributes,
      },
    ];

    for (const searchParams of searchPatterns) {
      try {
        const result = await searchClient.searchSingleIndex({
          indexName: indexNames.portfolios,
          searchParams: {
            ...searchParams,
            hitsPerPage: 20,
          },
        });

        if (result.nbHits > 0) {
          // Check if LinkedIn results have valid id field
          const validResults = result.hits.filter(
            (hit) => hit.id && hit.id.trim() !== "",
          );

          if (validResults.length > 0) {
            return {
              ...result,
              hits: validResults,
              nbHits: validResults.length,
            };
          }
          // If no valid ids, continue to next search pattern
        }
      } catch (error) {
        console.error("Additional LinkedIn search failed:", error);
        continue;
      }
    }

    return { nbHits: 0, hits: [] };
  };

  // Discover search: delegates entirely to the server-side API route.
  // OpenAI parses the query first, then Algolia (structured) then RC if needed.
  const performDiscoverSearch = async (
    query,
    {
      nameIndexesChecked = false,
      streamRcCount = false,
      onRcCount = null,
      broadenSearch = null,
      deferExternalUntilUserClick = true,
    } = {},
  ) => {
    try {
      await ensureSearchVisitorSession();
      const response = await fetch("/api/search/discover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          ...(deferExternalUntilUserClick
            ? {
                deferExternalUntilUserClick: true,
                skipExternalSearch: true,
              }
            : {}),
          ...(broadenSearch ? { broadenSearch } : {}),
          ...(nameIndexesChecked ? { nameIndexesChecked: true } : {}),
          ...(streamRcCount ? { streamRcCount: true } : {}),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData.error === "LOCATION_ONLY_SEARCH" ||
            errData.error === "SEARCH_REVISION_REQUIRED") {
          return { revisionRequired: errData.message };
        }
        const cooldown = readExternalSearchCooldown(errData);
        if (cooldown) {
          return {
            transformedResults: [],
            nbHits: 0,
            externalSearchUnavailable: true,
            retryInitialSearch: true,
            externalSearchCooldown: cooldown,
          };
        }
        if (response.status === 400) {
          if (
            errData?.error === "Search query flagged" ||
            errData?.flagged === true
          ) {
            return { flagged: true };
          }
        }
        return { transformedResults: [], nbHits: 0, externalSearchUnavailable: true, retryInitialSearch: true };
      }

      const data = await readSearchResponse(response, onRcCount);

      if (!data.success) {
        return { transformedResults: [], nbHits: 0, externalSearchUnavailable: true, retryInitialSearch: true };
      }

      // Keep parsed person intent for the shared people-list heading and filters
      const queryType = data.queryType || null;

      // Route returns 'transformedResults' for name/name+company paths and
      // 'results' for the main discover path. Support both.
      const cards = data.transformedResults || data.results || [];

      if (!cards.length) {
        return {
          transformedResults: [],
          nbHits: 0,
          moreResults: [],
          savedCursor: data.savedCursor || null,
          savedHasMore: data.savedHasMore === true,
          savedSearchUnavailable: data.savedSearchUnavailable === true,
          rawCandidates: data.rawCandidates || [],
          rcNextCursor: data.rcNextCursor || null,
          rcSearchFilters: data.rcSearchFilters || null,
          rcSearchDeferred: data.rcSearchDeferred === true,
          rcTotalCount: data.rcTotalCount ?? null,
          externalSearchUnavailable: data.externalSearchUnavailable === true,
          externalSearchCooldown: data.externalSearchCooldown || null,
          retryInitialSearch: data.externalSearchUnavailable === true && !data.rcSearchFilters && !data.savedCursor,
          deferExternalSearch: data.deferExternalSearch === true,
          showNarrowSearchHint: data.showNarrowSearchHint === true,
          ...(queryType ? { queryType } : {}),
          ...(data.parsedName ? { parsedName: data.parsedName } : {}),
          ...(data.parsedCompany ? { parsedCompany: data.parsedCompany } : {}),
          ...(data.suggestions?.length > 0
            ? {
                suggestions: data.suggestions,
                suggestionLabel: data.suggestionLabel || "",
              }
            : {}),
        };
      }

      return {
        transformedResults: cards,
        nbHits: cards.length,
        moreResults: data.moreResults || [],
        savedCursor: data.savedCursor || null,
        savedHasMore: data.savedHasMore === true,
        savedSearchUnavailable: data.savedSearchUnavailable === true,
        rawCandidates: data.rawCandidates || [],
        rcNextCursor: data.rcNextCursor || null,
        rcSearchFilters: data.rcSearchFilters || null,
        rcSearchDeferred: data.rcSearchDeferred === true,
        rcTotalCount: data.rcTotalCount ?? null,
        externalSearchUnavailable: data.externalSearchUnavailable === true,
        externalSearchCooldown: data.externalSearchCooldown || null,
        retryInitialSearch: data.externalSearchUnavailable === true && !data.rcSearchFilters && !data.savedCursor,
        deferExternalSearch: data.deferExternalSearch === true,
        showNarrowSearchHint: data.showNarrowSearchHint === true,
        ...(queryType ? { queryType } : {}),
        ...(data.parsedName ? { parsedName: data.parsedName } : {}),
        ...(data.parsedCompany ? { parsedCompany: data.parsedCompany } : {}),
      };
    } catch (error) {
      console.error("Discover search error:", error);
      return {
        transformedResults: [],
        nbHits: 0,
        moreResults: [],
        rawCandidates: [],
        externalSearchUnavailable: true,
        retryInitialSearch: true,
        showNarrowSearchHint: false,
      };
    }
  };

  // Counting never fetches people or enriches profiles.
  const fetchPeopleCount = async (query, filters) => {
    try {
      const response = await fetch("/api/search/discover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, countOnly: true, rcSearchFilters: filters }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.success && typeof data.rcTotalCount === "number" && data.rcTotalCount >= 0
        ? data.rcTotalCount : null;
    } catch {
      return null;
    }
  };

  // Continue the saved-profile search before requesting provider candidates.
  const loadMoreSavedProfiles = async (query, cursor, excluded = []) => {
    try {
      const response = await fetch("/api/search/discover", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, savedCursor: cursor, savedExcludeIds: excluded }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        return { profiles: [], savedCursor: cursor, unavailable: true,
          restartRequired: response.status === 400,
          revisionRequired: ["LOCATION_ONLY_SEARCH", "SEARCH_REVISION_REQUIRED"].includes(data.error) ? data.message : null };
      }
      const data = await response.json();
      if (data.success !== true) return { profiles: [], savedCursor: cursor, unavailable: true };
      return { profiles: data.savedResults || [], savedCursor: data.savedCursor || null,
        unavailable: data.savedSearchUnavailable === true };
    } catch {
      return { profiles: [], savedCursor: cursor, unavailable: true };
    }
  };

  // Fetch one 3-profile batch without running selected-person enrichment.
  const loadMoreRCCandidates = async (
    query,
    cursor = null,
    searchFilters = null,
    { onRcCount = null, maxCandidates = null } = {},
  ) => {
    try {
      await ensureSearchVisitorSession();
      const response = await fetch("/api/search/discover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          rawCandidatesOnly: !cursor,
          ...(Number.isInteger(maxCandidates) && maxCandidates > 0
            ? { externalPageSize: Math.min(3, maxCandidates) } : {}),
          ...(!cursor && onRcCount ? { streamRcCount: true } : {}),
          ...(cursor ? { rcCursor: cursor } : {}),
          ...(searchFilters ? { rcSearchFilters: searchFilters } : {}),
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const unavailable = [];
        unavailable.revisionRequired =
          ["LOCATION_ONLY_SEARCH", "SEARCH_REVISION_REQUIRED"].includes(data.error) ? data.message : null;
        unavailable.externalSearchUnavailable = true;
        unavailable.externalSearchCooldown =
          readExternalSearchCooldown(data);
        return unavailable;
      }
      const data = await readSearchResponse(response, onRcCount);
      if (!data.success) {
        const unavailable = [];
        unavailable.externalSearchUnavailable = true;
        return unavailable;
      }
      const candidates = data.rawCandidates || [];
      candidates.externalSearchUnavailable =
        data.externalSearchUnavailable === true;
      candidates.externalSearchCooldown =
        data.externalSearchCooldown || null;
      candidates.nextCursor = data.rcNextCursor || null;
      candidates.searchFilters = data.rcSearchFilters || searchFilters;
      candidates.totalCount = data.rcTotalCount ?? null;
      return candidates;
    } catch {
      const unavailable = [];
      unavailable.externalSearchUnavailable = true;
      return unavailable;
    }
  };

  // Enrich a single candidate on-demand (checks DB first, enriches only if new)
  const performEnrichOne = async (candidate) => {
    try {
      await ensureSearchVisitorSession();
      const response = await fetch("/api/search/enrich-one", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          linkedinUrl: candidate.linkedinUrl,
          firstName: candidate.firstName,
          lastName: candidate.lastName,
          company: candidate.company,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const cooldown = readExternalSearchCooldown(data);
        return cooldown ? { externalSearchCooldown: cooldown } : null;
      }
      const data = await response.json();
      return data.success
        ? { ...data.result, externalEnriched: data.source === "enriched" }
        : null;
    } catch {
      return null;
    }
  };

  return {
    performSearch,
    performDiscoverSearch,
    fetchPeopleCount,
    loadMoreSavedProfiles,
    loadMoreRCCandidates,
    performEnrichOne,
  };
};
